import { CART_EVENTS, MODAL_EVENTS } from "@app/lib/events";
import { Fetcher } from "@app/lib/fetch";
import { Cart, LineItem } from "@app/types";
import Alpine from "alpinejs";

import { formatCentsAsMoney } from "./utils";

type Options = {
  silent?: boolean;
};

export type AddLineItemData = {
  id: number;
  quantity: number;
  properties?: { [key: string]: string | undefined };
};

type ChangeLineItemData = {
  id?: number;
  line?: number;
  quatity: number;
  properties: Record<string, string | boolean | number>;
};

export const CART_ENDPOINTS = {
  GET: "/cart.js",
  CLEAR: "/cart/clear.js",
  ADD: "/cart/add.js",
  UPDATE: "/cart/update.js",
  CHANGE: "/cart/change.js",
};

type CartModified = {
  action: "added" | "updated" | "removed";
  item: LineItem;
};

export class CartFetcher extends Fetcher {
  // Static Values
  // Node IDs
  static ID__CART_COUNT = "cart-count";
  static ID__ADDED_TO_CART = "added-to-cart";

  static dispatchAddedToCart(items: LineItem[]) {
    document.dispatchEvent(
      new CustomEvent(CART_EVENTS.ADDED, {
        detail: {
          items,
        },
      }),
    );
  }

  static dispatchRemoveFromCart(id: number) {
    document.dispatchEvent(
      new CustomEvent(CART_EVENTS.REMOVE, {
        detail: {
          id,
        },
      }),
    );
  }

  static dispatchCartModified(item?: CartModified) {
    document.dispatchEvent(
      new CustomEvent(CART_EVENTS.MODIFIED, {
        detail: {
          item,
        },
      }),
    );
  }

  private cart: LineItem[];
  constructor() {
    super();
    this.addEventListeners();
    this.getCart().then((data) => data && (this.cart = data?.items ?? []));
  }

  addEventListeners() {
    document.addEventListener(CART_EVENTS.ADDED, () => {
      document.dispatchEvent(
        new CustomEvent(MODAL_EVENTS.OPEN, {
          detail: {
            target: CartFetcher.ID__ADDED_TO_CART,
          },
        }),
      );
    });
  }

  // API CALLS - 1:1 with Shopify Docs

  /**
   * Fetches the cart from Shopify asynchronously
   * https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
   */
  async getCart() {
    return await this.get<Cart>(CART_ENDPOINTS.GET);
  }

  /**
   * Clears Cart
   * Can only update 1 at a time
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */

  async clearCart() {
    const data = await this.post<Cart, object>(CART_ENDPOINTS.CLEAR, {});
    CartFetcher.dispatchCartModified();
    return data;
  }

  /**
   * Update line item in cart asynchronously
   * Can pass 1 line item or many
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
   */

  async updateCart(updates: UpdateCartData, action?: "remove" | "update") {
    const body = {
      updates,
    };

    const data = await this.post<Cart, typeof body>(CART_ENDPOINTS.UPDATE, body);
    const item = this.cart.find((item) => updates[item.id] !== undefined);
    if (!item) return data;

    if (action === "remove") {
      CartFetcher.dispatchCartModified({
        action: "removed",
        item,
      });
      return data;
    }

    CartFetcher.dispatchCartModified({
      action: "added",
      item,
    });
    return data;
  }

  async updateCartAttributes(attributes: Record<string, string>) {
    const body = {
      attributes,
    };

    const data = await this.post<Cart, typeof body>(CART_ENDPOINTS.UPDATE, body);
    CartFetcher.dispatchCartModified();
    return data;
  }

  /**
   * Adds line item to cart asynchronously
   * Can pass 1 line item or many
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   */

  async addLineItems(items: AddLineItemData | AddLineItemData[], options?: Options) {
    const { silent = false } = options ?? {};

    const body = {
      items: "length" in items ? items : [items],
    };

    type Response = {
      items: LineItem[];
    };

    /** Just testing the passing
     * the new item to the cart modified event. */
    const data = await this.post<Response, typeof body>(CART_ENDPOINTS.ADD, body);
    if (data && !silent) CartFetcher.dispatchAddedToCart(data.items);
    const itemId = items.id;
    const item = data?.items.find((item) => item.id.toString() === itemId?.toString()) as LineItem;
    this.cart = data?.items ?? [];

    CartFetcher.dispatchCartModified({
      action: "added",
      item,
    });
    return data;
  }

  /**
   * Changes line item in cart asynchronously
   * Can only update 1 at a time
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */

  async changeLineItem(item: ChangeLineItemData) {
    const data = await this.post<Cart, typeof item>(CART_ENDPOINTS.CHANGE, item);
    CartFetcher.dispatchCartModified();
    return data;
  }

  // Auxillary API handlers - wrappers for additional common cases

  async updateLineItemQuantity(id: number, quantity: number) {
    const action = quantity === 0 ? "remove" : "update";
    return await this.updateCart(
      {
        [id]: quantity,
      },
      action,
    );
  }

  async removeLineItem(id: number) {
    return await this.updateLineItemQuantity(id, 0);
  }

  async replaceLineItem(targetId: number, replacementId: number) {
    const options = { silent: true };
    const data = await this.getCart();
    if (!data) return;

    const targetLineItem = data.items.find((item) => item.id === targetId);
    if (!targetLineItem) return await this.addLineItems({ id: replacementId, quantity: 1 }, options);
    else await this.removeLineItem(targetLineItem.id);

    const existingLineItem = data.items.find((item) => item.id === replacementId);
    if (!existingLineItem)
      return await this.addLineItems({ id: replacementId, quantity: targetLineItem.quantity }, options);
    else return await this.updateLineItemQuantity(replacementId, existingLineItem.quantity + targetLineItem.quantity);
  }

  // Utility Methods
  formatLinePrice(price: number) {
    return formatCentsAsMoney(price);
  }
}

const cart = new CartFetcher();
export { cart };
